import {
  faCamera,
  faCloud,
  faImage,
  faSpinner,
  faTimes,
  faUpload,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { Employee } from 'data/users';
import { isImageFile, isVideoFile } from 'helpers/utils';
import { useSharePointFiles } from 'hooks/useSharePointFiles';
import { useAccessToken } from 'providers/AccessTokenProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import toast from 'react-hot-toast';
import { deleteFile } from 'services/api/file';
import {
  getJobFolderId,
  uploadImageToSharePoint
} from 'services/api/sharepoint';
import { JobFileListItem } from '../todo-list/JobFiles';
import { ImageUploadModalProps, MediaFile } from './types/types';

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
  show,
  onHide,
  jobId,
  files,
  setHasUpdated,
  onDeleteFile,
  jobNumbers = [],
  companyNames = []
}) => {
  const [selectedMedia, setSelectedMedia] = useState<MediaFile[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [visibleFiles, setVisibleFiles] = useState(files || []);

  const { authUser } = useAccessToken();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const {
    data: sharePointFiles,
    isLoading: isLoadingSharePoint,
    refetch: refetchSharePointFiles
  } = useSharePointFiles({
    jobNumbers,
    companyNames,
    enabled: show
  });

  const revokePreview = useCallback((media: MediaFile) => {
    if (media.type === 'video' && media.preview.startsWith('blob:')) {
      URL.revokeObjectURL(media.preview);
    }
  }, []);

  useEffect(() => {
    setVisibleFiles(files || []);
  }, [files]);

  useEffect(() => {
    return () => {
      selectedMedia.forEach(revokePreview);
    };
  }, [selectedMedia, revokePreview]);

  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;

      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(err => {
          console.error('useEffect: Error playing video:', err);
        });
      };
    }
  }, [showCamera, stream]);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const filesList = event.target.files;
      if (!filesList) return;

      const readers: Promise<MediaFile | null>[] = Array.from(filesList).map(
        (file, index) =>
          new Promise<MediaFile | null>(resolve => {
            const id = `${Date.now()}-${index}`;

            if (isImageFile(file)) {
              const reader = new FileReader();
              reader.onload = e => {
                resolve({
                  file,
                  preview: (e.target?.result as string) || '',
                  id,
                  type: 'image'
                });
              };
              reader.readAsDataURL(file);
            } else if (isVideoFile(file)) {
              const previewUrl = URL.createObjectURL(file);
              resolve({
                file,
                preview: previewUrl,
                id,
                type: 'video'
              });
            } else {
              resolve(null);
            }
          })
      );

      Promise.all(readers).then(newMedia => {
        const validMedia = newMedia.filter(
          (media): media is MediaFile => media !== null
        );
        if (validMedia.length === 0) {
          toast.error('Only image and video files are allowed.');
          return;
        }
        setSelectedMedia(prev => [...prev, ...validMedia]);
        event.target.value = '';
      });
    },
    []
  );

  const startCamera = useCallback(async () => {
    try {
      setCameraLoading(true);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      setStream(mediaStream);
      setShowCamera(true);
      setCameraLoading(false);
    } catch (error) {
      console.error('Camera error:', error);
      setCameraLoading(false);
      toast.error('Camera access denied or not available');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  }, [stream]);

  const captureImage = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      if (context) {
        context.drawImage(video, 0, 0);
        canvas.toBlob(
          blob => {
            if (blob) {
              const file = new File([blob], `camera-${Date.now()}.jpg`, {
                type: 'image/jpeg'
              });

              const mediaFile: MediaFile = {
                file,
                preview: canvas.toDataURL(),
                id: `camera-${Date.now()}`,
                type: 'image'
              };

              setSelectedMedia(prev => [...prev, mediaFile]);
              stopCamera();
            }
          },
          'image/jpeg',
          0.8
        );
      }
    }
  }, [stopCamera]);

  const removeMedia = useCallback(
    (id: string) => {
      setSelectedMedia(prev => {
        const mediaToRemove = prev.find(media => media.id === id);
        if (mediaToRemove) {
          revokePreview(mediaToRemove);
        }
        return prev.filter(media => media.id !== id);
      });
    },
    [revokePreview]
  );

  const clearSelectedMedia = useCallback(() => {
    setSelectedMedia(prev => {
      prev.forEach(revokePreview);
      return [];
    });
  }, [revokePreview]);

  const uploadSelectedMedia = useCallback(async () => {
    if (selectedMedia.length === 0) {
      setUploadError('Please select at least one file');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      if (!(authUser as any)?.id && !authUser?._id) {
        throw new Error('User not authenticated');
      }

      // SharePoint upload logic
      if (jobNumbers.length === 0 || companyNames.length === 0) {
        throw new Error(
          'Job numbers and company names are required for SharePoint upload'
        );
      }

      // Get job folder ID from SharePoint
      const { results } = await getJobFolderId(jobNumbers, companyNames);

      if (!results || results.length === 0 || !results[0].found) {
        throw new Error(
          'Job folder not found in SharePoint. Please ensure the job exists in SharePoint.'
        );
      }

      const jobFolder = results[0];
      const jobFolderId = jobFolder.folders[0]?.id;

      if (!jobFolderId) {
        throw new Error('Job folder ID not found');
      }

      // Upload media files to SharePoint (all to images folder)
      const uploadPromises = selectedMedia.map(async mediaFile => {
        const response = await uploadImageToSharePoint(
          jobFolderId,
          mediaFile.file
        );

        return {
          fileName: mediaFile.file.name,
          mimeType: mediaFile.file.type,
          jobId,
          _id: response.id,
          fileUrl: response.webUrl,
          creator: authUser as Employee,
          size: response.size.toString()
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      setHasUpdated?.(true);
      await refetchSharePointFiles();
      toast.success(
        `Successfully uploaded ${uploadedFiles.length} file(s) to SharePoint`
      );

      clearSelectedMedia();
      onHide();
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }, [
    selectedMedia,
    jobId,
    jobNumbers,
    companyNames,
    authUser,
    setHasUpdated,
    onHide,
    clearSelectedMedia
  ]);

  const handleClose = useCallback(() => {
    stopCamera();
    clearSelectedMedia();
    setUploadError(null);
    onHide();
  }, [stopCamera, onHide, clearSelectedMedia]);

  const imageCount = useMemo(
    () => selectedMedia.filter(media => media.type === 'image').length,
    [selectedMedia]
  );

  const videoCount = useMemo(
    () => selectedMedia.filter(media => media.type === 'video').length,
    [selectedMedia]
  );

  const selectedMediaLabel = useMemo(() => {
    if (selectedMedia.length === 0) return 'No files selected';
    const parts = [];
    if (imageCount)
      parts.push(`${imageCount} image${imageCount > 1 ? 's' : ''}`);
    if (videoCount)
      parts.push(`${videoCount} video${videoCount > 1 ? 's' : ''}`);
    return parts.join(' & ');
  }, [imageCount, videoCount, selectedMedia.length]);

  const handleExistingFileDelete = useCallback(
    async (fileId: string) => {
      try {
        if (onDeleteFile) {
          await onDeleteFile(fileId);
        } else {
          await deleteFile(fileId);
          toast.success('File deleted successfully');
        }

        setVisibleFiles(prev => prev.filter(file => file.id !== fileId));
        setHasUpdated?.(true);
      } catch (error: any) {
        console.error('Failed to delete file:', error);
        toast.error(error?.message || 'Failed to delete file');
      }
    },
    [onDeleteFile, setHasUpdated]
  );

  const allDisplayFiles = useMemo(() => {
    const dbFiles = visibleFiles.map(file => ({ ...file, source: 'gcp' }));
    const spFiles = (sharePointFiles || []).map((file: any) => ({
      id: file.id,
      fileName: file.name,
      mimeType: file.fileType,
      fileUrl: file.webUrl,
      creator: {
        _id: file.createdBy.user.id || 'sharepoint_user',
        firstName: file.createdBy.user.displayName.split(' ')[0] || '',
        lastName:
          file.createdBy.user.displayName.split(' ').slice(1).join(' ') || '',
        username: file.createdBy.user.email,
        email: file.createdBy.user.email,
        verified: true
      } as Employee,
      size: file.size.toString(),
      createdAt: file.lastModifiedDateTime,
      source: 'sharepoint',
      jobId: jobId
    }));

    return [...dbFiles, ...spFiles];
  }, [visibleFiles, sharePointFiles, jobId]);

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faCamera} className="me-2" />
          Upload Media
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!showCamera ? (
          <>
            {(allDisplayFiles.length > 0 || isLoadingSharePoint) && (
              <div className="mb-4">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">Existing Files</h6>
                  {isLoadingSharePoint && (
                    <div className="text-muted fs-10">
                      <Spinner animation="border" size="sm" className="me-1" />
                      Syncing SharePoint...
                    </div>
                  )}
                </div>
                <div
                  className="file-list-container"
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}
                >
                  <style>
                    {`
                      .file-list-container::-webkit-scrollbar {
                        display: none;
                      }
                    `}
                  </style>
                  {allDisplayFiles.map((attachment, index) => (
                    <div key={attachment.id} className="position-relative">
                      <JobFileListItem
                        attachment={attachment}
                        className={classNames({ 'border-top': index === 0 })}
                        onDeleteFile={
                          attachment.source === 'gcp'
                            ? handleExistingFileDelete
                            : undefined
                        }
                      />
                      {attachment.source === 'sharepoint' && (
                        <div
                          className="position-absolute top-0 end-0 mt-3 me-2"
                          title="Stored in SharePoint"
                        >
                          <Badge bg="info" className="fs-10">
                            <FontAwesomeIcon icon={faCloud} className="me-1" />
                            SharePoint
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Options */}
            <Row className="g-3 mb-4">
              <Col md={6}>
                <Button
                  variant="outline-primary"
                  className="w-100 h-100 py-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FontAwesomeIcon
                    icon={faUpload}
                    className="d-block mb-2 fs-2"
                  />
                  Upload from Device
                </Button>
              </Col>
              <Col md={6}>
                <Button
                  variant="outline-secondary"
                  className="w-100 h-100 py-4"
                  onClick={startCamera}
                >
                  <FontAwesomeIcon
                    icon={faCamera}
                    className="d-block mb-2 fs-2"
                  />
                  Capture Photo
                </Button>
              </Col>
            </Row>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Selected Images Preview */}
            {selectedMedia.length > 0 && (
              <div className="mb-4">
                <h6 className="mb-3">Selected Files ({selectedMediaLabel})</h6>
                <div
                  className="image-preview-container"
                  style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }}
                >
                  <style>
                    {`
                      .image-preview-container::-webkit-scrollbar {
                        display: none;
                      }
                    `}
                  </style>
                  <Row className="g-2">
                    {selectedMedia.map(mediaFile => (
                      <Col key={mediaFile.id} xs={6} md={4} lg={3}>
                        <Card className="position-relative">
                          {mediaFile.type === 'video' ? (
                            <div className="position-relative">
                              <video
                                src={mediaFile.preview}
                                controls
                                muted
                                className="w-100"
                                style={{ height: '120px', objectFit: 'cover' }}
                              />
                              <FontAwesomeIcon
                                icon={faVideo}
                                className="position-absolute top-0 start-0 m-2 text-white"
                              />
                            </div>
                          ) : (
                            <Card.Img
                              variant="top"
                              src={mediaFile.preview}
                              style={{ height: '120px', objectFit: 'cover' }}
                            />
                          )}
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0 m-1 btn-icon"
                            onClick={() => removeMedia(mediaFile.id)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                          <Card.Body className="p-2">
                            <small className="text-truncate d-block">
                              {mediaFile.file.name}
                            </small>
                            <Badge bg="secondary" className="fs-10">
                              {(mediaFile.file.size / 1024 / 1024).toFixed(2)}{' '}
                              MB
                            </Badge>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            )}

            {uploadError && (
              <Alert variant="danger" className="mb-3">
                {uploadError}
              </Alert>
            )}
          </>
        ) : (
          /* Camera View */
          <div className="text-center">
            {cameraLoading ? (
              <div className="py-5">
                <FontAwesomeIcon
                  icon={faSpinner}
                  spin
                  size="3x"
                  className="text-primary mb-3"
                />
                <p>Initializing camera...</p>
              </div>
            ) : (
              <>
                <div className="position-relative mb-3">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="rounded border"
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      minHeight: '300px',
                      backgroundColor: '#000'
                    }}
                    onCanPlay={() => console.log('Video can play')}
                    onError={e => console.error('Video error:', e)}
                  />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>

                <div className="d-flex justify-content-center gap-2">
                  <Button variant="primary" onClick={captureImage}>
                    <FontAwesomeIcon icon={faCamera} className="me-2" />
                    Capture
                  </Button>
                  <Button variant="secondary" onClick={stopCamera}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        {!showCamera && selectedMedia.length > 0 && (
          <Button
            variant="primary"
            onClick={uploadSelectedMedia}
            disabled={uploading}
          >
            {uploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Uploading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faImage} className="me-2" />
                Upload {selectedMedia.length} File(s)
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImageUploadModal;
